import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import { ListLayout } from "components/common/ListLayout";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { PAGELENGTH } from "components/common/ListLayout";
import { SubmitButton as Button } from "components/common/buttons/SubmitButton";
import {
  appointmentRequestSelector,
  fetchClientAppointmentRequests,
} from "slices/appointmentRequestSlice";
import { getEyeStyle, getBackgroundStyle, LegendButton } from "components/common/TableColor";
import { Link } from "react-router-dom";
import Api from "services/api";
import CancelModal from 'components/appointment/CancelModal';
import toast from "react-hot-toast";
import { Select, selectCustomStyles } from "components/common/Select";
import { AsyncPaginate } from "react-select-async-paginate";
import { fetchLanguages, languageSelector } from "slices/languageSlice";
// import AddAppointment from "components/appointment/AddAppointment";
import {
  appointmentsSelector,
  closeAppointmentPopup,
  setAddAppointmentPopup,
} from "slices/appointmentSlice";

function AppointmentRequests() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    appointmentRequests,
    last_page: totalPages,
    loader,
    total,
    current_page,
    per_page: pageLength,
  } = useSelector(appointmentRequestSelector);
  const { language: languages } = useSelector(languageSelector);

  const { user } = useSelector((state) => state.user);
  const { popup: appointmentPopUpState } = useSelector(appointmentsSelector);

  const handleClose = () => {
    dispatch(closeAppointmentPopup());
  };

  const callback = () => {
    handleClose();

    dispatch(
      fetchClientAppointmentRequests(currentPage, {
        ...filters,
        pageLength: pageLengthState,
        sortBy: sortConfig.sortBy,
        sortDirection: sortConfig.sortDirection,
        search: searchTerm,
      })
    );
  };

  const handleCreateRequest = async () => {
    const normalizedUser = user?.user ?? user;

    const defaultAccountLabel =
      normalizedUser?.name ||
      normalizedUser?.company_name ||
      normalizedUser?.full_name ||
      normalizedUser?.email;

    dispatch(
      setAddAppointmentPopup({
        show: true,
        item: {
          account_name: defaultAccountLabel,
        },
        popupType: "client-portal",
        callback,
      })
    );

    try {
      const response = await Api.clientAccountId(normalizedUser?.id);

      const subClient = response?.data?.data || [];

      dispatch(
        setAddAppointmentPopup({
          show: true,
          item: {
            account_id: subClient?.[0]?.id,
            account_name: defaultAccountLabel,
            sub_accounts: subClient?.[0]?.sub_accounts || [],
          },
          popupType: "client-portal",
          callback,
        })
      );
    } catch (error) {
      console.error(error);
      toast.error("Unable to load account information.");
    }
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    sortBy: "id",
    sortDirection: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [activeLegend, setActiveLegend] = useState(0);
  const [statusValue, setStatusValue] = useState("all");
  const [filters, setFilters] = useState({
    start_date: moment().startOf("month").format("YYYY-MM-DD"),
    end_date: moment().endOf("month").format("YYYY-MM-DD"),
    status: "all",
    language_id: "",
    service: "",
  });
  const [pageLengthState, setPageLengthState] = useState(PAGELENGTH);

  const { handleSubmit, control, reset } = useForm({
    defaultValues: {
      start_date: moment().startOf("month").format("YYYY-MM-DD"),
      end_date: moment().endOf("month").format("YYYY-MM-DD"),
      language_id: "",
      service: "",
    },
  });

  const statusLegends = [
    { id: 0, name: t("legend.all", "All"), value: "all", color: "#3176A0" },
    { id: 1, name: t("legend.newRequest", "New Request"), value: "request,New Request,Request", color: "#FFB82A" },
    { id: 2, name: t("legend.cancelled", "Cancelled"), value: "cancelled,Cancelled", color: "#DC2626" },
    { id: 3, name: t("legend.pendingLanguageApproval", "Pending Language Approval"), value: "Pending Language Approval", color: "#1AD598" },
  ];

  const serviceOptions = [
    { value: "In Person", label: t("service.inPerson", "In Person") },
    { value: "VRI", label: t("service.vri", "VRI") },
    { value: "Telephonic", label: t("service.telephonic", "Telephonic") },
  ];

  const filteredLanguages = React.useMemo(
    () =>
      languages?.map((language) => ({
        value: language?.id,
        label: language?.name,
      })),
    [languages]
  );

  const filteredLanguagesLoadOptions = async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    const result = await dispatch(
      fetchLanguages(
        page,
        {
          pageLength: 20,
          sortBy: "id",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      hasMore: page !== result?.last_page,
      additional: {
        page: page + 1,
      },
    };
  };

  useEffect(() => {
    dispatch(
      fetchClientAppointmentRequests(1, {
        pageLength: PAGELENGTH,
        start_date: filters.start_date,
        end_date: filters.end_date,
        status: filters.status,
        sortBy: sortConfig.sortBy,
        sortDirection: sortConfig.sortDirection,
      })
    );
  }, []);

  const [cancelModal, setCancelModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleOpenCancel = (item) => {
    setSelectedItem(item);
    setCancelModal(true);
  };

  const handleCloseCancel = () => {
    setCancelModal(false);
    setSelectedItem(null);
  };

  const handleCancelRequest = async (data) => {
    if (!selectedItem) return;
    setActionLoading(true);
    try {
      const response = await Api.clientCancelRequest(selectedItem.id, data.reason);
      if (response?.status >= 200 && response?.status < 300) {
        toast.success('Appointment request cancelled successfully!');
        handleCloseCancel();
        dispatch(
          fetchClientAppointmentRequests(currentPage, {
            ...filters,
            pageLength: pageLengthState,
            sortBy: sortConfig.sortBy,
            sortDirection: sortConfig.sortDirection,
          })
        );
      } else if (response?.status === 422) {
        toast.error(response.data.message || 'Validation failed');
      } else {
        toast.error(response?.data?.message || 'Failed to cancel appointment request');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error cancelling appointment request');
    }
    setActionLoading(false);
  };

  const handleActiveLegend = (id, value) => {
    setActiveLegend(id);
    setStatusValue(value);
    setCurrentPage(1);

    const newFilters = {
      ...filters,
      status: value,
      pageLength: pageLengthState,
      sortBy: sortConfig.sortBy,
      sortDirection: sortConfig.sortDirection,
    };
    setFilters(newFilters);
    dispatch(fetchClientAppointmentRequests(1, newFilters));
  };

  const onSubmit = (data) => {
    const newFilters = {
      start_date: data.start_date || "",
      end_date: data.end_date || "",
      status: statusValue,
      language_id: data.language_id || "",
      service: data.service || "",
      pageLength: pageLengthState,
      sortBy: sortConfig.sortBy,
      sortDirection: sortConfig.sortDirection,
    };
    setFilters(newFilters);
    setCurrentPage(1);
    dispatch(fetchClientAppointmentRequests(1, newFilters));
  };

  const handleResetFields = () => {
    const defaultStartDate = moment().startOf("month").format("YYYY-MM-DD");
    const defaultEndDate = moment().endOf("month").format("YYYY-MM-DD");
    reset({
      start_date: defaultStartDate,
      end_date: defaultEndDate,
      language_id: "",
      service: "",
    });
    setActiveLegend(0);
    setStatusValue("all");

    const newFilters = {
      start_date: defaultStartDate,
      end_date: defaultEndDate,
      status: "all",
      language_id: "",
      service: "",
      pageLength: pageLengthState,
      sortBy: sortConfig.sortBy,
      sortDirection: sortConfig.sortDirection,
    };
    setFilters(newFilters);
    setCurrentPage(1);
    dispatch(fetchClientAppointmentRequests(1, newFilters));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    dispatch(
      fetchClientAppointmentRequests(page, {
        ...filters,
        pageLength: pageLengthState,
        sortBy: sortConfig.sortBy,
        sortDirection: sortConfig.sortDirection,
      })
    );
  };

  const handlePageLengthChange = (length) => {
    setPageLengthState(length);
    dispatch(
      fetchClientAppointmentRequests(1, {
        ...filters,
        pageLength: length,
        sortBy: sortConfig.sortBy,
        sortDirection: sortConfig.sortDirection,
      })
    );
  };

  const renderFilters = () => {
    return (
      <div className="w-100">
        <div className="button-sm forLegends mb-3">
          <LegendButton
            activeId={activeLegend}
            options={statusLegends}
            handleClick={handleActiveLegend}
          />
        </div>
        <form className="search_form_ad">
          <div className="row-primary dahboard_filter" style={{ marginBottom: 10 }}>
            <div className="row-primary">
              <h2>{t("filter.startDate", "Start Date")}</h2>
              <Controller
                name="start_date"
                control={control}
                render={({ field }) => (
                  <div className="input-image basic_bases">
                    <input
                      type="date"
                      {...field}
                      value={field.value ? moment(field.value).format("YYYY-MM-DD") : ""}
                      onChange={(e) => {
                        const dateString = moment(new Date(e.target.value)).format("YYYY/MM/DD");
                        field.onChange(dateString);
                      }}
                    />
                    <img src="/images/celender.png" alt="calendar" className="custom-icon" />
                  </div>
                )}
              />
            </div>
            <div className="row-primary">
              <h2>{t("filter.endDate", "End Date")}</h2>
              <Controller
                name="end_date"
                control={control}
                render={({ field }) => (
                  <div className="input-image basic_bases">
                    <input
                      type="date"
                      {...field}
                      value={field.value ? moment(field.value).format("YYYY-MM-DD") : ""}
                      onChange={(e) => {
                        const dateString = moment(new Date(e.target.value)).format("YYYY/MM/DD");
                        field.onChange(dateString);
                      }}
                    />
                    <img src="/images/celender.png" alt="calendar" className="custom-icon" />
                  </div>
                )}
              />
            </div>
            <div className="row-primary">
              <h2>{t("filter.language", "Language")}</h2>
              <Controller
                name="language_id"
                control={control}
                render={({ field }) => (
                  <div className="basic_bases">
                    <AsyncPaginate
                      menuPlacement="auto"
                      styles={{ ...selectCustomStyles }}
                      value={
                        field.value
                          ? filteredLanguages?.find((opt) => field.value == opt.value)
                          : ""
                      }
                      loadOptions={filteredLanguagesLoadOptions}
                      isClearable
                      escapeClearsValue
                      onChange={(obj) => {
                        field.onChange(obj?.value || "");
                      }}
                      additional={{ page: 1 }}
                    />
                  </div>
                )}
              />
            </div>
            <div className="row-primary">
              <h2>{t("filter.serviceType", "Service Type")}</h2>
              <Controller
                name="service"
                control={control}
                render={({ field }) => (
                  <div className="basic_bases">
                    <Select
                      name="service"
                      options={serviceOptions}
                      {...field}
                      value={
                        field.value
                          ? serviceOptions?.find((opt) => field.value == opt.value)
                          : ""
                      }
                    />
                  </div>
                )}
              />
            </div>
          </div>
          <div className="row-primary btns_area row_10 mb-0">
            <SubmitButton handleSubmit={() => handleSubmit(onSubmit)()} buttonType="button" btnText={t("button.filter", "Filter")} className="green" />
            <SubmitButton buttonType="button" className="lightBlue" btnText={t("button.reset", "Reset")} handleSubmit={handleResetFields} />
          </div>
        </form>
      </div>
    );
  };

  const renderTableHeader = React.useCallback(() => {
    const callBack = (dir, sortBy) => {
      setSortConfig({ sortBy, sortDirection: dir });
      dispatch(
        fetchClientAppointmentRequests(currentPage, {
          ...filters,
          pageLength: pageLengthState,
          sortBy,
          sortDirection: dir,
          search: searchTerm,
        })
      );
    };

    return (
      <tr>
        <th>#</th>
        <th>
          <TableHeaderItem title={t("table.appRequestId", "App Request Id")} sortBy="appid" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        <th>
          <TableHeaderItem title={t("table.status", "Status")} sortBy="status" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        <th>
          <TableHeaderItem title={t("table.service", "Service")} sortBy="type" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        <th>
          <TableHeaderItem title={t("table.date", "Date")} sortBy="date" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        <th>
          <TableHeaderItem title={t("table.language", "Language")} sortBy="language_id" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        {/* <th>Action</th> */}
      </tr>
    );
  }, [currentPage, filters, pageLengthState, searchTerm, sortConfig, t]);

  const renderTableContent = React.useCallback(() => {
    return appointmentRequests?.map((item, index) => {
      const serial = index + 1 + (current_page - 1) * pageLength;
      const style = getBackgroundStyle(item?.status);
      return (
        <tr key={item?.id || index} style={style}>
          {/* <td style={style}>{serial}</td> */}
          <td className='p-0' style={style}>
              <div className={`eye_area  ${item?.appointment_details?.extra_mileage_request == 1 && 'extra-mileage'}`} style={getEyeStyle(item?.status, style)}>
                  <Link to={`/user/appointment-requests/view/${item?.id}`}><i class="fa-solid fa-eye"></i></Link>
              </div>
          </td>
          <td style={style}>{item?.appid || "N/A"}</td>
          <td style={style}>{item?.status == 'cancelled' ? (item?.request_details?.cancelled_by === 'mb' ? t("status.cancelledByMb", "Cancelled by MB") : t("legend.cancelled", "Cancelled")) : (item?.status ? t(`status.${item.status}`, item.status) : "N/A")}</td>
          <td style={style}>{item?.type === 'Telephonic' ? t("service.telephonic", "Telephonic") : (item?.type ? t(`service.${item.type}`, item.type) : 'N/A')}</td>
          <td style={style}>{moment(item?.datetime, "YYYY-MM-DD hh:mm A").format("MMM D, YYYY hh:mm A") || "N/A"}</td>
          <td style={style}>{item?.language?.name ? item?.language?.name : item?.language_requests?.[0]?.language_name || "N/A"}</td>
          {/* <td style={style}>
            <div className="row-primary">
              {!item?.appointment_id && item?.status !== 'cancelled' && (
                <button className="main_button tomatoRed" onClick={() => handleOpenCancel(item)} style={{ marginLeft: 8 }} disabled={actionLoading}>
                  {actionLoading ? 'Cancelling...' : 'Cancel Request'}
                </button>
              )}
            </div>
          </td> */}
        </tr>
      );
    });
  }, [appointmentRequests, current_page, pageLength]);

  return (
    <>
    <ListLayout
      heading={t("sidebar.appointmentRequests", "Appointment Requests")}
      title=""
      buttonTitle={t("button.createRequest", "Create Request")}
      addButtonDisabled={false}
      handleAddPopUp={handleCreateRequest}
      disableBootstrap={true}
      renderTableHeader={renderTableHeader}
      renderTableContent={renderTableContent}
      renderFilters={renderFilters}
      totalPages={totalPages}
      fetchList={fetchClientAppointmentRequests}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      loader={loader}
      noOfItems={total}
      hideHeader={false}
      searchPlaceholder={t("table.searchAppointmentRequests", "Search Appointment Requests ...")}
      hidePagination={false}
      pageLengthChange={handlePageLengthChange}
      paginationParams={filters}
      defaultPageLength={PAGELENGTH}
      sortConfig={sortConfig}
    />
    <CancelModal
      isOpen={cancelModal}
      onClose={handleCloseCancel}
      onConfirm={handleCancelRequest}
      appointmentId={selectedItem?.appid || selectedItem?.id}
      item={selectedItem}
      loader={actionLoading}
      heading="Request"
    />
    </>
  );
}

export default AppointmentRequests;
